import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Calculator, Clock, User, FileText, TrendingUp, TrendingDown, CheckCircle2,
  XCircle, AlertTriangle, Search, Filter, RefreshCw, Eye, Coins, Banknote,
  Calendar, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import CashClosingValidationModal from '../components/CashClosingValidationModal';

const CashClosingHistoryPage = () => {
  const { user, api } = useAuth();
  
  const [closings, setClosings] = useState([]);
  const [pendingClosings, setPendingClosings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClosing, setSelectedClosing] = useState(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const isManager = ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(user?.role?.toUpperCase());
  
  useEffect(() => {
    loadData();
  }, [statusFilter]);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const [historyRes, pendingRes] = await Promise.all([
        api().get('/cash-closing/history', { params }),
        isManager ? api().get('/cash-closing/pending') : Promise.resolve({ data: [] })
      ]);
      
      setClosings(historyRes.data || []);
      setPendingClosings(pendingRes.data || []);
    } catch (error) {
      console.error('Error loading closings:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  const formatCFA = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' F';
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getStatusBadge = (status) => {
    const configs = {
      pending: { label: 'En attente', color: 'bg-yellow-500', icon: Clock },
      recount_requested: { label: 'Recomptage demandé', color: 'bg-orange-500', icon: RefreshCw },
      validated: { label: 'Validée', color: 'bg-green-500', icon: CheckCircle2 },
      rejected: { label: 'Rejetée', color: 'bg-red-500', icon: XCircle }
    };
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={cn(config.color, "text-white")}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };
  
  const handleViewClosing = (closing) => {
    setSelectedClosing(closing);
    setShowValidationModal(true);
  };
  
  const filteredClosings = closings.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      c.register_name?.toLowerCase().includes(query) ||
      c.created_by_name?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calculator className="w-6 h-6 text-primary" />
            Historique des Clôtures
          </h1>
          <p className="text-muted-foreground">
            Consultez l'historique des clôtures de caisse
          </p>
        </div>
        
        <Button onClick={loadData} variant="outline" disabled={loading}>
          <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
          Actualiser
        </Button>
      </div>
      
      {/* Pending Closings for Managers */}
      {isManager && pendingClosings.length > 0 && (
        <Card className="border-yellow-300 bg-yellow-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-yellow-700">
              <AlertTriangle className="w-5 h-5" />
              Clôtures en attente de validation ({pendingClosings.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingClosings.map(closing => (
                <div 
                  key={closing.id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border cursor-pointer hover:border-primary transition-colors"
                  onClick={() => handleViewClosing(closing)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                      <Coins className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="font-bold">{closing.register_name}</p>
                      <p className="text-sm text-muted-foreground">
                        Par {closing.created_by_name} • {formatDate(closing.created_at)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Écart</p>
                      <p className={cn(
                        "font-bold",
                        closing.variance === 0 && "text-green-600",
                        closing.variance > 0 && "text-blue-600",
                        closing.variance < 0 && "text-red-600"
                      )}>
                        {closing.variance >= 0 ? '+' : ''}{formatCFA(closing.variance)}
                      </p>
                    </div>
                    {getStatusBadge(closing.status)}
                    <Button size="sm" variant="outline">
                      <Eye className="w-4 h-4 mr-1" />
                      Voir
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par caisse ou utilisateur..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="validated">Validées</SelectItem>
                <SelectItem value="rejected">Rejetées</SelectItem>
                <SelectItem value="recount_requested">Recomptage demandé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Closings List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Toutes les clôtures ({filteredClosings.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : filteredClosings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Aucune clôture trouvée</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[500px]">
              <div className="space-y-3">
                {filteredClosings.map(closing => (
                  <div 
                    key={closing.id}
                    className="p-4 border rounded-lg hover:border-primary/50 transition-colors cursor-pointer"
                    onClick={() => handleViewClosing(closing)}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      {/* Left - Info */}
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-12 h-12 rounded-lg flex items-center justify-center",
                          closing.status === 'validated' && "bg-green-100",
                          closing.status === 'rejected' && "bg-red-100",
                          closing.status === 'pending' && "bg-yellow-100",
                          closing.status === 'recount_requested' && "bg-orange-100"
                        )}>
                          <Calculator className={cn(
                            "w-6 h-6",
                            closing.status === 'validated' && "text-green-600",
                            closing.status === 'rejected' && "text-red-600",
                            closing.status === 'pending' && "text-yellow-600",
                            closing.status === 'recount_requested' && "text-orange-600"
                          )} />
                        </div>
                        
                        <div>
                          <p className="font-bold">{closing.register_name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <User className="w-3 h-3" />
                            <span>{closing.created_by_name}</span>
                            <span>•</span>
                            <Calendar className="w-3 h-3" />
                            <span>{formatDate(closing.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Middle - Amounts */}
                      <div className="flex items-center gap-6 text-sm">
                        <div className="text-center">
                          <p className="text-muted-foreground">Compté</p>
                          <p className="font-bold">{formatCFA(closing.counted_total)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-muted-foreground">Théorique</p>
                          <p className="font-bold text-blue-600">{formatCFA(closing.theoretical_cash)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-muted-foreground">Écart</p>
                          <p className={cn(
                            "font-bold flex items-center justify-center gap-1",
                            closing.variance === 0 && "text-green-600",
                            closing.variance > 0 && "text-blue-600",
                            closing.variance < 0 && "text-red-600"
                          )}>
                            {closing.variance > 0 ? <TrendingUp className="w-3 h-3" /> : 
                             closing.variance < 0 ? <TrendingDown className="w-3 h-3" /> : 
                             <CheckCircle2 className="w-3 h-3" />}
                            {closing.variance >= 0 ? '+' : ''}{formatCFA(closing.variance)}
                          </p>
                        </div>
                      </div>
                      
                      {/* Right - Status & Action */}
                      <div className="flex items-center gap-3">
                        {getStatusBadge(closing.status)}
                        <Button size="sm" variant="ghost">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Validated Info */}
                    {closing.status === 'validated' && closing.validated_by_name && (
                      <div className="mt-2 pt-2 border-t text-xs text-muted-foreground flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-green-600" />
                        Validée par {closing.validated_by_name} le {formatDate(closing.validated_at)}
                      </div>
                    )}
                    
                    {/* Variance Reason */}
                    {closing.variance_reason && (
                      <div className="mt-2 pt-2 border-t text-xs">
                        <span className="text-muted-foreground">Motif: </span>
                        <span>{closing.variance_reason}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
      
      {/* Validation Modal */}
      <CashClosingValidationModal
        open={showValidationModal}
        onClose={() => {
          setShowValidationModal(false);
          setSelectedClosing(null);
        }}
        closing={selectedClosing}
        api={api}
        onValidationComplete={loadData}
      />
    </div>
  );
};

export default CashClosingHistoryPage;
